import type { Enum } from '@shared-types/enum.types';
import type { RoundPhase } from '@shared-types/match.types';
import { formatUrl } from '@utilities/fetch';
import { Warn } from '@utilities/logging';
import { competitionId as defaultCompetitionId, uefaApiBaseUrl } from '@utilities/WindowContext';

export const LINKRULE_TYPE = {
  COMPETITION: 'competition',
  MATCH: 'match',
  H2H: 'h2h',
  ARTICLE: 'article',
  VIDEO: 'video',
  PLAYER: 'player',
  TEAM: 'team',
  DRAW: 'draw',
} as const;
export type LINKRULE_TYPE = Enum<typeof LINKRULE_TYPE>;

const _getUrl = (
  type: LINKRULE_TYPE,
  id: string,
  competitionId: string,
  phase: RoundPhase,
  historical: boolean,
  subPage: string,
) => {
  if (!id) return '#';

  try {
    const url = formatUrl(uefaApiBaseUrl, `linkrules/${type}/${id}/${subPage ?? ''}`, {
      competitionId,
      phase,
      historical,
    });
    return url?.toString() ?? '#';
  } catch (e) {
    Warn(e);
  }

  return '#';
};

export const getCompetitionUrl = (id: string, phase?: RoundPhase, subPage = '') =>
  _getUrl(LINKRULE_TYPE.COMPETITION, id, undefined, phase, false, subPage);

export const getMatchUrl = (id: string, subPage = '') =>
  _getUrl(LINKRULE_TYPE.MATCH, id, undefined, undefined, false, subPage);

export const getArticleUrl = (id: string, subPage = '') =>
  _getUrl(LINKRULE_TYPE.ARTICLE, id, undefined, undefined, false, subPage);

export const getVideoUrl = (id: string, subPage = '') =>
  _getUrl(LINKRULE_TYPE.VIDEO, id, undefined, undefined, false, subPage);

export const getPlayerUrl = (id: string, competitionId = defaultCompetitionId, phase?: RoundPhase, subPage = '') =>
  _getUrl(LINKRULE_TYPE.PLAYER, id, competitionId, phase, false, subPage);

export const getTeamUrl = (
  id: string,
  competitionId = defaultCompetitionId,
  historical = false,
  phase?: RoundPhase,
  subPage = '',
) => _getUrl(LINKRULE_TYPE.TEAM, id, competitionId, phase, historical, subPage);

export const getDrawUrl = (roundId: string, competitionId = defaultCompetitionId, subPage = '') =>
  _getUrl(LINKRULE_TYPE.DRAW, roundId, competitionId, undefined, false, subPage);

export const getHeadToHeadUrl = (
  id1: string,
  id2: string,
  competitionId = defaultCompetitionId,
  historical = false,
  subPage = '',
) => _getUrl(LINKRULE_TYPE.H2H, `${id1}/${id2}`, competitionId, undefined, historical, subPage);
